// src/App.js
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import DefaultLayout from "./layouts";
import { routes } from "./routes";
import { Fragment, useCallback, useEffect } from "react";
import SignIn from "./pages/signIn";
import ForgotPassword from "./pages/forgotPassword";
import { useDispatch, useSelector } from "react-redux";
import { getMessagingToken, onForegroundMessage } from "./services/firebase";
import { setMessage, setToken } from "./redux/slices/fcmSlice";
import toast from "react-hot-toast";
import ChangePassword from "./pages/changePassword";
import EmployeeApp from "./employeeApp/EmployeeApp";
import AffiliateRegister from "./pages/affiliateRegister";
import { AFFILIATE_BASE_PATH } from "./utils/affiliate";

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.login?.currentUser);
  const roles = user?.roles || [];

  useEffect(() => {
    if (
      location.pathname !== "/forgot" &&
      location.pathname !== "/change-password" &&
      !location.pathname.startsWith("/nv") &&
      !location.pathname.startsWith(AFFILIATE_BASE_PATH) &&
      !user?.accessToken
    ) {
      document.cookie = "accessTokenForgot=; path=/; max-age=0";
      navigate("/login");
    }
  }, [dispatch, navigate, user, location.pathname]);

  const hasPermission = useCallback(
    (routeRoles) => {
      if (!routeRoles || routeRoles.length === 0 || roles?.includes("ALL")) {
        return true;
      }
      return routeRoles.some((role) => roles?.includes(role));
    },
    [roles]
  );

  const filteredRoutes = routes.filter((route) => hasPermission(route.role));

  useEffect(() => {
    (async () => {
      try {
        const token = await getMessagingToken();
        if (token) {
          dispatch(setToken(token));
        }
      } catch (err) {
        console.log("Lỗi lấy token:", err);
      }
    })();

    const employeeId = user?.user?._id;

    const handleMessage = (payload) => {
      if (!employeeId) return;
      console.log("[FCM] foreground/background payload:", payload);
      toast("Bạn có thông báo mới", { icon: "🔔" });
      dispatch(setMessage({ ...payload, employeeId }));
    };

    // 1) Tin khi tab đang mở
    let unsubscribe = () => {};
    onForegroundMessage((payload) => handleMessage(payload)).then((u) => {
      unsubscribe = typeof u === "function" ? u : () => {};
    });

    // 2) Tin từ SW qua BroadcastChannel (khi còn tab)
    const channel = new BroadcastChannel("notifications");
    const listener = (event) => {
      const data = event?.data;
      if (data) handleMessage(data);
    };
    channel.addEventListener("message", listener);

    return () => {
      try { unsubscribe?.(); } catch {}
      channel.removeEventListener("message", listener);
      channel.close();
    };
  }, [dispatch, user]);

  // Trang đăng ký qua link giới thiệu (affiliate) là trang công khai:
  // người mới chưa có tài khoản nên không đi qua luồng đăng nhập.
  if (location.pathname.startsWith(AFFILIATE_BASE_PATH)) {
    return (
      <Routes>
        <Route
          path={`${AFFILIATE_BASE_PATH}/:code`}
          element={<AffiliateRegister />}
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Cổng nhân viên (FEv1) chạy hoàn toàn độc lập với app admin:
  // auth/token/route riêng, không đụng redux store hay luồng đăng nhập admin.
  if (location.pathname.startsWith("/nv")) {
    return <EmployeeApp />;
  }

  return (
    <Routes>
      {user?.accessToken ? (
        filteredRoutes.map((route, index) => {
          const Layout = route.isShowHeader ? DefaultLayout : Fragment;
          const Page = route.page;

          return (
            <Route
              key={index}
              path={route.path}
              element={
                <Layout>
                  <Page />
                </Layout>
              }
            />
          );
        })
      ) : location.pathname === "/forgot" ? (
        <Route path="forgot" element={<ForgotPassword />} />
      ) : location.pathname === "/change-password" ? (
        <Route path="change-password" element={<ChangePassword />} />
      ) : (
        <Route path="*" element={<SignIn />} />
      )}
    </Routes>
  );
}

export default App;
